import { mapState } from 'vuex'
import { sumBy, filter } from 'lodash'
import accounting from 'accounting'

export default {
  computed: {
    ...mapState('job', ['job']),
    ...mapState('load', ['loads']),

    haveJob () {
      return this.job.id !== null &&
        this.job.id !== undefined
    },

    haveNoJob () {
      return !this.haveJob
    },

    jobIsCompleted () {
      return this.haveJob &&
        (this.job.status === 'completed' || this.job.status === 'invoiced')
    },

    jobIsNotCompleted () {
      return this.haveJob &&
        this.job.status !== 'completed' &&
        this.job.status !== 'invoiced'
    },

    jobIsFinalising () {
      return this.haveJob &&
        this.job.status === 'finalising'
    },

    jobIsNotFinalising () {
      return this.haveJob &&
        this.job.status !== 'finalising'
    },

    jobIsPending () {
      return this.haveJob &&
        this.job.status === 'pending'
    },

    jobIsNotPending () {
      return this.haveJob &&
        this.job.status !== 'pending'
    },

    jobIsCancelled () {
      return this.haveJob &&
        this.job.status === 'cancelled'
    },

    jobIsNotCancelled () {
      return this.haveJob &&
        this.job.status !== 'cancelled'
    },

    jobIsEditable () {
      return this.jobIsNotCancelled && this.jobIsNotCompleted &&
        (this.$user.exists === false || this.$user.isEnabled)
    },

    jobIsNotEditable () {
      return !this.jobIsEditable
    },

    jobCommissionAsMoney () {
      return accounting.formatNumber(this.jobCommission, 2)
    },

    /**
     * Get the job commission for the selected coordinator.
     *
     * @return void
     */
    jobCommission () {
      const filteredLoads = filter(this.loads, load => load.status !== 'cancelled')

      const getLoadSize = load => {
        let size = 0

        if (load.weighbridge && load.weighbridge.id !== null && load.weighbridge.status === 'approved') {
          size = load.weighbridge.total_tonnage
        }

        return size // a load has no weighbridges should be ignored
      }

      // Get the total profit of all the loads by using the load size (or weighbridge tonnage if available)
      // and multiplying by the loads rate.
      let loadTotalProfit = sumBy(filteredLoads, load => getLoadSize(load) * load.rate)

      // Get the total profit of the job by multiplying the jobs rate by the loads size (or weighbridge
      // tonnage if available).
      let jobTotalProfit = this.job.rate * sumBy(filteredLoads, getLoadSize)

      let additionalChargesTotalProfit = sumBy(filteredLoads, load => {
        let loadSize = getLoadSize(load)

        return sumBy(load.charges, charge => {
          if (charge.type === 'single') {
            return charge.client_rate - charge.carrier_rate
          }

          return (charge.client_rate - charge.carrier_rate) * loadSize
        })
      })

      return (jobTotalProfit - loadTotalProfit) + additionalChargesTotalProfit
    },

    /**
     * Determine if job commission is profitable.
     *
     * @return boolean
     */
    jobCommissionIsProfitable () {
      return this.jobCommission > 0
    },

    /**
     * Determine if job commission is not profitable.
     *
     * @return boolean
     */
    jobCommissionIsNotProfitable () {
      return this.jobCommission <= 0
    },

    /**
     * Get the coordinators commission from the jobs commission.
     *
     * @return float
     */
    coordinatorCommission () {
      let coordinatorTotal = 0

      // Difference of jobTotalProfit and loadTotalProfit (how much arrow earns) multiplied the coordinator's
      // commission percentage over 100. This should return what amount the coordinator earns out of
      // the amount arrow earns from the job.
      if (this.job.coordinator) {
        coordinatorTotal = this.jobCommission * (this.job.coordinator.profile.commission_amount / 100)
      }

      return coordinatorTotal
    },

    /**
     * Get the coordinator commission as money.
     *
     * @return float
     */
    coordinatorCommissionAsMoney () {
      return accounting.formatNumber(this.coordinatorCommission, 2)
    },

    /**
     * Determine if coordinator commission is profitable.
     *
     * @return boolean
     */
    coordinatorCommissionIsProfitable () {
      return this.coordinatorCommission > 0
    },

    /**
     * Determine if coordinator commission is not profitable.
     *
     * @return boolean
     */
    coordinatorCommissionIsNotProfitable () {
      return this.coordinatorCommission <= 0
    },

    /**
     * Running total of all approved loads by summing the additional charges
     * and taking the weighbridge tonnage and multiplying it by the rate.
     *
     * @return string
     */
    jobRunningTotal () {
      return accounting.formatMoney(sumBy(this.loads, load => {
        if (load.weighbridge === null || load.weighbridge.status !== 'approved') {
          return
        }

        let additionalChargesTotal = sumBy(load.charges, charge => {
          if (charge.type === 'single') {
            return charge.client_rate
          }

          return charge.client_rate * load.weighbridge.total_tonnage
        })

        return ((parseFloat(this.job.rate) || 0) * load.weighbridge.total_tonnage) + additionalChargesTotal
      }))
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/jobs/job-helpers.js